import DownloadIcon from '@mui/icons-material/Download';
import FileDownloadDoneIcon from '@mui/icons-material/FileDownloadDone';
import {
  Box,
  Button,
  Checkbox,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Typography
} from '@mui/material';
import Chip from '@mui/material/Chip';
import React, { useState } from 'react';
import { getQuestions, getUserResponses } from '../../../../utils/mutations/form';
import { getProducts } from '../../../../utils/mutations/products';
import { Product, Question, User } from '../../../../utils/types';
import TableWidget from '../../../widgets/TableWidget';
import UserModal from './UserModal';

interface RegistrantTabProps {
  registrants: User[];
}

const RegistrantTab: React.FC<RegistrantTabProps> = ({ registrants }: RegistrantTabProps) => {
  const [filterTicketClasses, setFilterTicketClasses] = useState<string[]>([]);
  const [downloaded, setDownloaded] = useState<boolean>(false);
  const rows = registrants
    .filter((user)=>(filterTicketClasses.length === 0 || filterTicketClasses.includes(user.ticketClass ?? '')))
    .slice()
    .sort((a, b) => (b.paymentTime?.getTime() || 0) - (a.paymentTime?.getTime() || 0))
    .map((user, i) => [
      i+1,
      user.displayName,
      user.paymentTime?.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }) ?? '',
      user.email,
      user.ticketClass,
      <UserModal user={user} />
   ]);

  const uniqueTicketClasses: string[] = Array.from(new Set(
    registrants
      .map(user => user.ticketClass)
      .filter((ticketClass): ticketClass is string => ticketClass !== null)
  ));

  const handleChangeFilterClass = (event: any) => {
    const selectedClasses = event.target.value as string[];
    setFilterTicketClasses(selectedClasses);
  };

  const handleDownload = async () => {
    // Fetch all form questions for the conference
    const conferenceCode = registrants[0]?.conferenceCode;
    if (!conferenceCode) {
      alert('No conference code found for registrants.');
      return;
    }
    const questions: Question[] = await getQuestions(conferenceCode);

    // Filter out hline
    const filteredQuestions: Question[] = questions.filter(q => q.question.trim() !== "");

    // Find unique product IDs from registrants
    const products: Product[] = await getProducts(conferenceCode);

    // Build CSV headers
    const columnNames = [
      'DisplayName',
      'Email',
      'UID',
      'TicketClass',
      'PaymentTime',
      ...filteredQuestions.map((q: Question) => q.question.replace(/"/g, '""')),
      ...products.map((p: Product) => p.name.replace(/"/g, '""')),
    ];

    // For each registrant, fetch their responses and products
    const csvData = await Promise.all(
      registrants.map(async (registrant: User) => {
        const { displayName, email, uid, ticketClass } = registrant;
        const paymentTime = registrant.paymentTime
          ? registrant.paymentTime.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          : '';
        let responses: Record<string, any> = {};

        try {
          responses = (await getUserResponses(uid)) || {};
        } catch {
          responses = {};
        }

        // Build row with user info, then each question's answer, then exec seminar
        const row = [
          displayName,
          email,
          uid,
          ticketClass,
          paymentTime,
          ...filteredQuestions.map(q => {
            const ans = responses[q.id];
            if (typeof ans === 'string') return ans.replace(/"/g, '""');
            if (ans == null) return '';
            return JSON.stringify(ans).replace(/"/g, '""');
          }),
          ...products.map((product: Product) => {
            // Get the quantity of the product purchased by the registrant
            return registrant.products[product.id] || 0;
          }),
        ];
        return row;
      })
    );

    // Combine column names and registrants data
    const csvString = `${columnNames.map(cell => `"${cell}"`).join(',')}
${csvData.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n')}`;
    
    // Create a Blob with the CSV data
    const blob = new Blob([csvString], { type: 'text/csv' });
  
    // Create a download URL for the Blob
    const url = window.URL.createObjectURL(blob);
  
    // Create an anchor element
    const a = document.createElement('a');
    a.href = url;
    a.download = 'registrants.csv';
  
    // Append the anchor element to the document
    document.body.appendChild(a);
  
    // Programmatically trigger a click on the anchor element
    a.click();
    window.URL.revokeObjectURL(url);
  
    // Remove the anchor element from the document
    document.body.removeChild(a);
  
    setDownloaded(true);
  }

  return (
    <Box sx={{ width: '100%', overflow: 'auto', marginBottom: '20px', padding: '20px' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', marginBottom: '10px' }}>
        {registrants?.length} registrants
      </Typography>
      <Typography variant="body2">Filter by ticket class</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '200px' }}>
          <Select
            value={filterTicketClasses}
            onChange={handleChangeFilterClass}
            size='small'
            displayEmpty
            multiple
            input={<OutlinedInput />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.sort().map((value) => (
                  <Chip key={value} label={value} />
                ))}
              </Box>
            )}
          >
            {uniqueTicketClasses.map((ticketClass: string) => (
              <MenuItem key={ticketClass} value={ticketClass}>
                <Checkbox checked={filterTicketClasses.includes(ticketClass)} />
                <ListItemText primary={ticketClass} />
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Button
          variant="text"
          startIcon={downloaded ? <FileDownloadDoneIcon /> : <DownloadIcon />}
          onClick={handleDownload}
          sx={{ color: 'black' }}
        >
          Download
        </Button>
      </Box>
      <TableWidget rows={rows} search={true} headers={["#", "Name", "Date", "Email", "Class", ""]} />
    </Box>
  );  
};

export default RegistrantTab;
